/**
 * Thin helpers around @simplewebauthn/browser for Celus WebAuthn 2FA.
 */
import {
  WebAuthnAbortService,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

/** Must match the `max_length` of the credential name on the backend */
export const WEBAUTHN_NAME_MAX_LENGTH = 64;

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined"
  );
}

export async function webAuthnRegister(
  optionsJSON: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  return startRegistration({ optionsJSON });
}

/**
 * With `autofill`, no prompt is shown immediately. The ceremony stays pending
 * until the user picks a passkey from the browser / password manager autofill
 * offered on an input with `autocomplete="... webauthn"` (conditional UI).
 */
export async function webAuthnAuthenticate(
  optionsJSON: PublicKeyCredentialRequestOptionsJSON,
  { autofill = false }: { autofill?: boolean } = {},
): Promise<AuthenticationResponseJSON> {
  return startAuthentication({
    optionsJSON,
    useBrowserAutofill: autofill,
    // the input may not be rendered yet; the browser just waits for it
    verifyBrowserAutofillInput: false,
  });
}

export async function isWebAuthnAutofillSupported(): Promise<boolean> {
  return isWebAuthnSupported() && (await browserSupportsWebAuthnAutofill());
}

/** Abort a pending ceremony, e.g. the autofill one */
export function cancelWebAuthnCeremony(): void {
  WebAuthnAbortService.cancelCeremony();
}

/** The user dismissed or aborted the browser prompt. */
export function isWebAuthnCancellation(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === "AbortError" || error.name === "NotAllowedError")
  );
}
